import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { Constants, pageInContext, pdfLabelsSubmitDetails, transactionDetailLabels, transactionTypes } from "projects/money-movement-ui/src/app/shared/utilities/Constants";
import { combineLatest, Subject, takeUntil, withLatestFrom } from "rxjs";
import { selectAchInfo, selectCheckTaxWithholding, selectInternalTransferInfo, selectIraFieldsIsIraTransaction, selectIs401kToAccount, selectIsBundledOneTimeTotalDist, selectStagedTransactionForm, selectStateCode, selectTwePopupIndicator, selectWireInfo } from "../../../../../create-request/reducers";
import { AccountInfoActions } from "projects/money-movement-ui/src/app/account-info-header/actions";
import { selectNraIndicator } from "projects/money-movement-ui/src/app/account-info-header/reducers";
import { FormatPercentPipe } from "projects/money-movement-ui/src/app/shared/pipes/FormatPercentPipe";
import { selectInitReleaseTransactionInContext } from "projects/money-movement-ui/src/app/initiate-and-release/reducers";
import { selectViewInContext } from "projects/money-movement-ui/src/app/shared/reducers";
import { AchExtraPrompts, AchTransactionDetails, ChecksExtraPrompts } from "../../../../../create-request/models/form/TransactionForm";

@Component({
  standalone: false,
    selector: 'mm-twe-details-section',
    templateUrl: './twe-details-section.component.html',
    styleUrls: ['./twe-details-section.component.scss']
})

export class TweDetailsSectionComponent implements OnInit, OnDestroy {
    @Input() transactionType: string;
    
    readonly labels = transactionDetailLabels;
    readonly pdfLabels = pdfLabelsSubmitDetails;
    readonly taxCopyParagraphOne: string = Constants.taxCopyParagraphOne;
    readonly taxCopyParagraphTwo: string = Constants.taxCopyParagraphTwo;

    federalTax: string;
    federalTaxAmount: string;
    stateTax: string;
    stateTaxLabel: string = this.labels.stateTax;
    stateTaxAmount: string;
    distributionReason: string;
    contributionReason: string;
    transferDirection: string;
    
    destroy$: Subject<void> = new Subject<void>();
    isIraTransaction: boolean;
    isOneTimeTotalDist: boolean = false;
    is401kToAccount: boolean;
    private viewInContext: string;
    private openTwePopup: boolean = false;

    constructor(private store: Store) {}

    ngOnInit(): void {

        this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe(view => this.viewInContext = view);
        this.store.select(selectTwePopupIndicator).pipe(takeUntil(this.destroy$)).subscribe(value => this.openTwePopup = value);
        this.store.select(selectIraFieldsIsIraTransaction).pipe(takeUntil(this.destroy$)).subscribe(_isIraTransaction => this.isIraTransaction = _isIraTransaction)
        const formatPercent = new FormatPercentPipe;
        if (this.transactionType === transactionTypes.check) {
            combineLatest([
                this.store.select(selectCheckTaxWithholding),
                this.store.select(selectInitReleaseTransactionInContext),
                this.store.select(selectStagedTransactionForm),
                this.store.select(selectIsBundledOneTimeTotalDist)
            ]).pipe(takeUntil(this.destroy$)).subscribe(([twe, inr, form, isBundledOneTimeTotalDist]) => {
 
                const extraPrompts = form?.extraPrompts as ChecksExtraPrompts;
                this.isOneTimeTotalDist = extraPrompts?.frequency === 'onetimeTotalDist' || isBundledOneTimeTotalDist;
                this.federalTax = formatPercent.transform(twe.fedTaxPercent);
                this.federalTaxAmount = this.removePercent(twe.fedTaxAmountFormatted ?? '');
                this.stateTax = formatPercent.transform(twe.stateTaxPercent);
                this.stateTaxAmount = this.removePercent(twe.stateTaxAmountFormatted ?? '');
                this.distributionReason = inr?.draftObject?.distributionReasonCdValue ?? twe.distributionReason ?? '';
                this.syncHeaderTaxRates();
            });
        } else if (this.transactionType === transactionTypes.ach) {

            combineLatest([
                this.store.select(selectAchInfo),
                this.store.select(selectStagedTransactionForm)
            ]).pipe(takeUntil(this.destroy$)).subscribe(([achInfo, form]) => {
                const transactionDetails = form.transactionDetails as AchTransactionDetails;
                const extraPrompts = form.extraPrompts as AchExtraPrompts;
                this.contributionReason = transactionDetails?.contributionReason;
                this.federalTaxAmount = achInfo.fedTaxAmount?.split(' ')[0];
                this.stateTaxAmount = achInfo.stateTaxAmount?.split(' ')[0];
                this.federalTax = achInfo.fedTaxPercent ?? '';
                this.stateTax = achInfo.stateTaxPercent ?? '';
                this.distributionReason = achInfo.distributionReason ? achInfo.distributionReason :  form?.transactionDetails?.taxWithholdingsFormGroup?.distributionReason ?? '';
                this.transferDirection = extraPrompts?.transferDirection;
                this.syncHeaderTaxRates();
            });
        } else if (this.transactionType === transactionTypes.internalTransfer) {
            this.store.select(selectInternalTransferInfo)
            .pipe(takeUntil(this.destroy$), withLatestFrom(this.store.select(selectIs401kToAccount))
            ).subscribe(([itInfo, is401kToAccount]) => {
                this.is401kToAccount = is401kToAccount;
                this.federalTaxAmount = this.getTaxAmount(itInfo.iraFields.fedTaxAmount);
                this.stateTaxAmount = this.getTaxAmount(itInfo.iraFields.stateTaxAmount);
                this.federalTax = this.isIraTransaction ? formatPercent.transform(itInfo.iraFields.fedTaxPercent) : '';
                this.stateTax = this.isIraTransaction ? formatPercent.transform(itInfo.iraFields.stateTaxPercent) : '';
                this.distributionReason =  itInfo.iraFields.distributionReason.description || itInfo?.inputIraFields?.distributionReason?.description;
                this.syncHeaderTaxRates();
            });
        } else if (this.transactionType === transactionTypes.wire) {
            this.store.select(selectWireInfo)
            .pipe(takeUntil(this.destroy$)).subscribe((wireInfo) => {
                this.federalTaxAmount = wireInfo.fedTaxAmount;
                this.stateTaxAmount = wireInfo.stateTaxAmount;
                this.federalTax = formatPercent.transform(wireInfo.fedTaxPercent ?? '');
                this.stateTax = formatPercent.transform(wireInfo.stateTaxPercent ?? '');
                this.distributionReason = wireInfo.distributionReason.description ?? '';
                this.syncHeaderTaxRates();
            });
        }

        combineLatest([
            this.store.select(selectStateCode),
            this.store.select(selectNraIndicator)
        ]).pipe(takeUntil(this.destroy$)).subscribe(([stateCode, nraIndicator]) => {
            if (stateCode && !nraIndicator) {
                this.stateTaxLabel = stateCode === " " ? `${this.labels.stateTax}` : `${this.labels.stateTax} (${stateCode})`;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    removePercent(amount: string): string {
        let amountArray = amount.split('(');
        if (amountArray.length > 1) {
            return amountArray[0].trim();
        }
        return amount;
    }

    getTaxAmount(tax: string): string {
        return tax?.split(' ')[0];
    }

    syncHeaderTaxRates(): void {
        if (this.viewInContext === pageInContext.confirmation && this.openTwePopup) {
            this.store.dispatch(AccountInfoActions.updateHeaderTaxRates({
                federalTaxRate: this.federalTax,
                stateTaxRate: this.stateTax
            }));
        }
    }
}